import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { AppRole } from '@/types/roles';
import { ROLE_PERMISSIONS, CHAT_PERSONAS, ROLE_LABELS } from '@/types/roles';
import type { RolePermissions, ChatPersona } from '@/types/roles';
import { setSandboxActive } from '@/lib/sandboxGuard';

interface SandboxState {
  /** Whether sandbox mode is active */
  isActive: boolean;
  /** The role being impersonated */
  impersonatedRole: AppRole | null;
  /** Label for the impersonated member */
  impersonatedLabel: string | null;
  /** The real user ID of the member being impersonated */
  impersonatedUserId: string | null;
  /** Legacy slot key — now set to the impersonated user ID */
  sandboxSlotKey: string | null;
  /** Permissions for the impersonated role */
  sandboxPermissions: RolePermissions | null;
  /** Chat persona for the impersonated role */
  sandboxPersona: ChatPersona | null;
  /** The space ID the user was on before entering sandbox */
  previousSpaceId: string | null;
  /** The sandbox space ID (once resolved) */
  sandboxSpaceId: string | null;
  /** Activate sandbox as a specific real member */
  enterSandbox: (role: AppRole, userId: string, displayName: string) => void;
  /** Deactivate sandbox mode */
  exitSandbox: () => void;
  /** Called by the host page to register the sandbox space once created */
  setSandboxSpaceId: (id: string) => void;
  /** Called by the host page to store the previous space before switching */
  setPreviousSpaceId: (id: string | undefined) => void;
}

const SandboxContext = createContext<SandboxState>({
  isActive: false,
  impersonatedRole: null,
  impersonatedLabel: null,
  impersonatedUserId: null,
  sandboxSlotKey: null,
  sandboxPermissions: null,
  sandboxPersona: null,
  previousSpaceId: null,
  sandboxSpaceId: null,
  enterSandbox: () => {},
  exitSandbox: () => {},
  setSandboxSpaceId: () => {},
  setPreviousSpaceId: () => {},
});

export function SandboxProvider({ children }: { children: ReactNode }) {
  const [impersonatedRole, setImpersonatedRole] = useState<AppRole | null>(null);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [customLabel, setCustomLabel] = useState<string | null>(null);
  const [previousSpaceId, setPreviousSpaceId] = useState<string | null>(null);
  const [sandboxSpaceId, setSandboxSpaceId] = useState<string | null>(null);

  const enterSandbox = useCallback((role: AppRole, userId: string, displayName: string) => {
    setImpersonatedRole(role);
    setImpersonatedUserId(userId);
    setCustomLabel(displayName);
    (window as any).__workitect_sandbox_label = displayName;
    window.dispatchEvent(new CustomEvent('workitect-sandbox-persona-changed', { detail: { role, userId, displayName } }));
  }, []);

  const exitSandbox = useCallback(() => {
    setImpersonatedRole(null);
    setImpersonatedUserId(null);
    setCustomLabel(null);
    delete (window as any).__workitect_sandbox_label;
    // Publish actor restoration just like entry publishes impersonation. The
    // project loader consumes this as a hard actor boundary and immediately
    // clears projects, tasks, selections, and realtime subscriptions.
    window.dispatchEvent(new CustomEvent('workitect-sandbox-persona-changed', {
      detail: { role: null, userId: null, displayName: null },
    }));
  }, []);

  const isActive = impersonatedRole !== null;

  // Mirror sandbox active state into the module-level guard so non-React
  // code paths (RPC wrappers, automations) can short-circuit when needed.
  useEffect(() => {
    setSandboxActive(isActive, customLabel);
    return () => setSandboxActive(false, null);
  }, [isActive, customLabel]);


  return (
    <SandboxContext.Provider
      value={{
        isActive,
        impersonatedRole,
        impersonatedLabel: customLabel ?? (impersonatedRole ? ROLE_LABELS[impersonatedRole] : null),
        impersonatedUserId,
        sandboxSlotKey: impersonatedUserId,
        sandboxPermissions: impersonatedRole ? ROLE_PERMISSIONS[impersonatedRole] : null,
        sandboxPersona: impersonatedRole ? CHAT_PERSONAS[impersonatedRole] : null,
        previousSpaceId,
        sandboxSpaceId,
        enterSandbox,
        exitSandbox,
        setSandboxSpaceId,
        setPreviousSpaceId: (id: string | undefined) => setPreviousSpaceId(id ?? null),
      }}
    >
      {children}
    </SandboxContext.Provider>
  );
}

export function useSandbox() {
  return useContext(SandboxContext);
}

/** The well-known name for the sandbox space */
export const SANDBOX_SPACE_NAME = '🧪 Sandbox';
export const SANDBOX_SPACE_COLOR = '#F59E0B';
export const SANDBOX_SPACE_ICON = 'code';

/** The well-known name for the demo space */
export const DEMO_SPACE_NAME = '🎬 Demos';
export const DEMO_SPACE_COLOR = '#EC4899';
export const DEMO_SPACE_ICON = 'clapperboard';
